import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from "firebase/auth";

// Toggle emulator connections: set REACT_APP_DISABLE_FIREBASE_EMULATORS=true in your .env to disable
const DISABLE_FIREBASE_EMULATORS = process.env.REACT_APP_DISABLE_FIREBASE_EMULATORS === 'true';
const FIREBASE_DEBUG_LOGS =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_FIREBASE_DEBUG_LOGS === "true";

const debugLog = (...args) => {
  if (FIREBASE_DEBUG_LOGS) {
    console.log(...args);
  }
};

const debugWarn = (...args) => {
  if (FIREBASE_DEBUG_LOGS) {
    console.warn(...args);
  }
};

const debugError = (...args) => {
  if (FIREBASE_DEBUG_LOGS) {
    console.error(...args);
  }
};

// ✅ Firebase Configuration from environment
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// 🔍 Debugging: Firebase Initialization Check
debugLog("🔍 Checking Firebase Initialization...");

// ✅ Ensure Firebase App is Initialized Once
let app;
try {
    app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    debugLog("🚀 Firebase app initialized successfully.");
} catch (error) {
    debugError("❌ Firebase app initialization failed:", error);
}

// 🔄 Initialize Firebase Services with Debugging
let db, storage, auth;

try {
    db = getFirestore(app);
    debugLog("📂 Firestore initialized successfully.");
} catch (error) {
    debugError("❌ Firestore initialization failed:", error);
}

try {
    storage = getStorage(app);
    debugLog("🗂️ Storage initialized successfully.");
} catch (error) {
    debugError("❌ Storage initialization failed:", error);
}

try {
    auth = getAuth(app);
    debugLog("🔑 Authentication initialized successfully.");

    // Force Firebase Auth to use localStorage for persistence
    setPersistence(auth, browserLocalPersistence)
      .then(() => debugLog("✅ Auth persistence set to localStorage"))
      .catch((err) => debugError("❌ Failed to set auth persistence:", err));
} catch (error) {
    debugError("❌ Authentication initialization failed:", error);
}

// ✅ Enable Firebase Emulators in Local Development
if (process.env.NODE_ENV === "development" && !DISABLE_FIREBASE_EMULATORS) {
    debugWarn("⚠️ Running in Development Mode: Connecting to Firebase Emulators...");

    try {
        connectFirestoreEmulator(db, "localhost", 8080);
        debugLog("🔥 Firestore Emulator connected on http://localhost:8080");
    } catch (error) {
        debugError("❌ Firestore Emulator connection failed:", error);
    }

    try {
        // Disable the built‑in emulator UI warning banner
        connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: false });
        debugLog("🔐 Auth Emulator connected on http://localhost:9099");
    } catch (error) {
        debugError("❌ Auth Emulator connection failed:", error);
    }

    try {
        connectStorageEmulator(storage, "localhost", 9199);
        debugLog("📦 Storage Emulator connected on http://localhost:9199");
    } catch (error) {
        debugError("❌ Storage Emulator connection failed:", error);
    }
}

// ✅ Final Debug Log before Export
debugLog("📡 Exporting Firebase services...");
debugLog("🔥 Firebase Config Loaded Successfully - Debug Logs Enabled.");

// 📤 Export Firebase Services
export { app, db, storage, auth };
